import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { currentRole, isLivyAdmin, livyUnavailableMessage } from 'src/components/livy/access';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CONSTANTS } from '@livable/saas-react-components';
import { Box, Button, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { LivyHelp } from 'src/components/livy/LivyHelp';
import { LivyReports } from 'src/components/livy/LivyReports';
import { useBillingType } from 'src/components/livy/billingType';
import { LivyPanel } from 'src/components/livy/LivyPanel';
import { LivyAvatar } from 'src/components/livy/LivyAvatar';
import { selectedCompanyName, useLivyChat } from 'src/components/livy/useLivyChat';
import { useResetOnCompanyChange } from 'src/components/livy/useResetOnCompanyChange';
import { livyConfig } from 'src/lib/livyTransport';
import { LivyHistory } from 'src/components/livy/LivyHistory';
import { isReplayConversation } from 'src/components/livy/history';
import { useLivyVoice } from 'src/components/livy/useLivyVoice';
import { LivyCallBar, LivyCallButton } from 'src/components/livy/LivyCallBar';
import { isLiveActive } from 'src/components/livy/useLivyChat';
import { usePageAsk } from 'src/components/livy/pageAsk';
import { LivyTheme } from 'src/components/livy/LivyTheme';
import { LivyCustomerPage } from './LivyCustomerPage';
import { freshToken, tokenForCompany } from 'src/lib/livyApi';
import { LivyPreviewBanner, LivyResidentBanner } from 'src/components/livy/LivyPreviewBanner';
import { LivyResidentPicker } from 'src/components/livy/LivyResidentPicker';
import { LivyCompanyPicker } from 'src/components/livy/LivyCompanyPicker';
import { LivySurfacePicker, SURFACE_STARTERS, SURFACE_SUBTITLE, isPreviewSurface, runSurface, surfaceFromRun } from 'src/components/livy/LivySurfacePicker';
const STARTERS = ['Which allocations are still pending?', 'Recovery rate by property this month', 'Which utilities jumped vs last cycle?', 'What is blocking billing from closing?'];

/**
 * Full-page Livy. Admin-gated for the initial rollout, every surface ships behind
 * LivableAdmin/LivableSuperAdmin and un-gates progressively.
 */
function LivyPageBody() {
  const companyLabel = selectedCompanyName();
  const isAdmin = isLivyAdmin(currentRole());
  const context = useMemo(() => ({
    route: '/livy'
  }), []);
  const _usePageAsk = usePageAsk(),
    _usePageAsk2 = _slicedToArray(_usePageAsk, 2),
    ask = _usePageAsk2[0],
    clearAsk = _usePageAsk2[1];
  const _useState = useState('admin'),
    _useState2 = _slicedToArray(_useState, 2),
    view = _useState2[0],
    setView = _useState2[1];
  // freshToken, not the raw store: a streaming turn never passes through the app's refresh
  // interceptor. Every preview also moves the token onto the company the app has selected.
  const preview = isPreviewSurface(view);
  const getToken = useCallback(() => preview ? tokenForCompany() : freshToken(), [preview]);
  // A resumed thread keeps the surface it ran on (the drawer's staff surface is not admin) until
  // the view changes or a new chat starts.
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    resumedOn = _useState4[0],
    setResumedOn = _useState4[1];
  const surface = resumedOn !== null && resumedOn !== void 0 ? resumedOn : view;
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    historyOpen = _useState6[0],
    setHistoryOpen = _useState6[1];
  const _useState7 = useState('chat'),
    _useState8 = _slicedToArray(_useState7, 2),
    tab = _useState8[0],
    setTab = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    queuedReport = _useState0[0],
    setQueuedReport = _useState0[1];
  const billingType = useBillingType();
  // The resident the resident support view runs as. The proxy and MCP service prove it is the selected company's.
  const _useState1 = useState(null),
    _useState10 = _slicedToArray(_useState1, 2),
    picked = _useState10[0],
    setPicked = _useState10[1];
  const resident = useMemo(() => view === 'resident-support' && picked ? {
    residencyId: picked.residencyId,
    accountId: picked.accountId
  } : null, [view, picked]);
  const config = livyConfig();
  const _useLivyChat = useLivyChat({
      // A null config means Livy is not deployed in this environment; the gate below renders first.
      config: config !== null && config !== void 0 ? config : {
        endpoint: '',
        region: '',
        identityPoolId: '',
        userPoolId: ''
      },
      getToken,
      context,
      surface,
      resident
    }),
    messages = _useLivyChat.messages,
    streaming = _useLivyChat.streaming,
    error = _useLivyChat.error,
    send = _useLivyChat.send,
    stop = _useLivyChat.stop,
    reset = _useLivyChat.reset,
    retry = _useLivyChat.retry,
    resume = _useLivyChat.resume,
    conversationId = _useLivyChat.conversationId,
    live = _useLivyChat.live,
    startLive = _useLivyChat.startLive;
  // A report is its own conversation: sent once the reset has emptied the transcript.
  useEffect(() => {
    if (!queuedReport || messages.length > 0) return;
    setQueuedReport(null);
    void send(queuedReport);
  }, [queuedReport, messages.length, send]);
  const voice = useLivyVoice({
    config,
    getToken,
    surface,
    conversationId,
    resident,
    send,
    streaming
  });
  const endCall = voice.end;
  // A call belongs to its conversation: a new chat, a resumed one or another surface hangs it up.
  useEffect(() => () => void endCall(), [conversationId, endCall]);
  // A resident belongs to one company: switching company drops the pick with the chat.
  const resetAll = useCallback(() => {
    void endCall('company');
    reset();
    setPicked(null);
  }, [reset, endCall]);
  useResetOnCompanyChange(resetAll);
  const startOver = useCallback(() => {
    setPicked(null);
    setResumedOn(null);
    reset();
  }, [reset]);

  // Asks made while on this page arrive from the launcher as router state (usePageAsk). Each is sent
  // once, then cleared, so a refresh does not re-ask; the guard resets once it is gone.
  const asked = useRef(false);
  useEffect(() => {
    if (!ask) {
      asked.current = false;
      return;
    }
    // Authorisation before the request, not after the render. This effect runs above the guard
    // below, so without the role check a non-admin following a deep link fired a real turn at the
    // proxy while the page told them Livy was unavailable.
    // Held while a reply streams (the hook drops a send then) and sent once it finishes.
    if (asked.current || !config || !isAdmin || streaming) return;
    // A palette ask is about a record, not the picked resident: it leaves the resident view for Admin and asks there.
    if (view === 'resident-support') {
      setView('admin');
      setPicked(null);
      setResumedOn(null);
      reset();
      return;
    }
    // A kept backtest replay is read-only, so an ask starts a new chat on the same view.
    if (isReplayConversation(conversationId)) {
      setResumedOn(null);
      reset();
      return;
    }
    asked.current = true;
    clearAsk();
    send(ask);
  }, [ask, clearAsk, send, config, isAdmin, streaming, view, reset, conversationId]);

  // The same read-only rule as the panel below, plus a live handoff: a person from Livable holds the chat.
  const readOnly = view === 'resident-support' && (!picked || resumedOn === 'resident-support');
  let callBlocked;
  if (readOnly || isReplayConversation(conversationId)) callBlocked = 'Start a new chat to call.';else if (isLiveActive(live)) callBlocked = 'A person from Livable is in this chat.';
  const handleCall = () => {
    // Made in the click so the browser lets it play.
    void voice.start(new Audio());
  };
  if (!isAdmin || !config) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        p: '24px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1"
    }, livyUnavailableMessage));
  }
  return (
    /*#__PURE__*/
    // The shell reserves PAGE_BOTTOM_PADDING under every page, which on a chat surface is dead
    // space between the composer and the window. Grow into it and pull the margin back, so the
    // transcript uses the full height instead of ending 80px short of the bottom.
    React.createElement(Stack, {
      sx: {
        height: "calc(100% + ".concat(CONSTANTS.PAGE_BOTTOM_PADDING, "px)"),
        mb: "-".concat(CONSTANTS.PAGE_BOTTOM_PADDING, "px"),
        minHeight: 0
      }
    }, /*#__PURE__*/React.createElement(Paper, {
      variant: "outlined",
      sx: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        borderWidth: '0 0 0 1px'
      }
    }, /*#__PURE__*/React.createElement(Box, {
      sx: {
        px: '16px',
        py: '12px',
        borderBottom: 1,
        borderColor: 'divider'
      }
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      alignItems: "center",
      justifyContent: "space-between",
      sx: {
        flexWrap: 'wrap',
        columnGap: '12px',
        rowGap: '10px'
      }
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "10px",
      alignItems: "center",
      sx: {
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement(LivyAvatar, {
      size: 30,
      thinking: streaming
    }), /*#__PURE__*/React.createElement(Box, {
      sx: {
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      sx: {
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: '-.01em',
        lineHeight: 1.25
      }
    }, "Livy"), companyLabel && /*#__PURE__*/React.createElement(Typography, {
      noWrap: true,
      sx: {
        fontSize: 11.5,
        color: 'text.secondary'
      }
    }, view === 'resident-support' && picked ? "".concat(picked.label, " \xB7 ").concat(companyLabel) : companyLabel))), /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      alignItems: "center",
      sx: {
        flexWrap: 'wrap',
        gap: '8px'
      }
    }, !(view === 'resident-support' && !picked) && !voice.active && /*#__PURE__*/React.createElement(LivyCallButton, {
      disabledReason: callBlocked,
      onCall: handleCall
    }), view === 'resident-support' && picked && /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: startOver,
      disabled: streaming,
      sx: {
        textTransform: 'none',
        fontSize: 12.5,
        minWidth: 0,
        px: '10px'
      }
    }, "Change resident"), /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => setHistoryOpen(true)
      // Resuming mid-stream replaces the transcript while the live stream keeps writing
      // into it, so the old conversation's tokens land in the resumed one.
      ,
      disabled: streaming,
      sx: {
        textTransform: 'none',
        fontSize: 12.5,
        minWidth: 0,
        px: '10px'
      }
    }, "History"), /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => {
        setResumedOn(null);
        reset();
      },
      disabled: messages.length === 0 || streaming,
      sx: {
        textTransform: 'none',
        fontSize: 12.5,
        minWidth: 0,
        px: '10px'
      }
    }, "New chat"), /*#__PURE__*/React.createElement(LivySurfacePicker, {
      value: view,
      onChange: next => {
        setView(next);
        setPicked(null);
        setResumedOn(null);
        // A new surface is a new conversation: carrying admin answers into a customer
        // preview would show a customer things their surface cannot reach.
        reset();
        // So is an ask still held for the old one's reply: it would land in the new surface.
        if (ask) clearAsk();
      }
    })))), /*#__PURE__*/React.createElement(LivyCallBar, {
      state: voice.state,
      muted: voice.muted,
      seconds: voice.seconds,
      maxSeconds: voice.maxSeconds,
      error: voice.error,
      endedBy: voice.endedBy,
      onMute: voice.mute,
      onEnd: () => void voice.end()
    }), preview && view !== 'resident-support' && /*#__PURE__*/React.createElement(LivyPreviewBanner, {
      companyLabel: companyLabel
    }), view === 'resident-support' && picked && /*#__PURE__*/React.createElement(LivyResidentBanner, {
      name: picked.label
    }), /*#__PURE__*/React.createElement(Tabs, {
      value: tab,
      onChange: (_, next) => setTab(next),
      sx: {
        px: '16px',
        minHeight: 36,
        borderBottom: 1,
        borderColor: 'divider',
        '& .MuiTab-root': {
          minHeight: 36,
          py: 0,
          textTransform: 'none'
        }
      }
    }, /*#__PURE__*/React.createElement(Tab, {
      value: "chat",
      label: "Chat"
    }), /*#__PURE__*/React.createElement(Tab, {
      value: "help",
      label: "Help",
      disabled: streaming
    }), /*#__PURE__*/React.createElement(Tab, {
      value: "reports",
      label: "Reports",
      disabled: streaming
    })), tab === 'reports' ? /*#__PURE__*/React.createElement(LivyReports, {
      billingType: billingType(),
      onRun: prompt => {
        setResumedOn(null);
        reset();
        setTab('chat');
        setQueuedReport(prompt);
      }
    }) : tab === 'help' ? /*#__PURE__*/React.createElement(LivyHelp, {
      billingType: billingType(),
      onAsk: text => {
        setTab('chat');
        void send(text);
      }
    }) : view === 'resident-support' && !picked && messages.length === 0 ? /*#__PURE__*/React.createElement(Stack, {
      alignItems: "center",
      justifyContent: "center",
      spacing: "14px",
      sx: {
        flex: 1,
        p: '24px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary",
      align: "center",
      sx: {
        maxWidth: 420
      }
    }, SURFACE_SUBTITLE['resident-support']), /*#__PURE__*/React.createElement(LivyCompanyPicker, {
      companyLabel: companyLabel
    }), /*#__PURE__*/React.createElement(LivyResidentPicker, {
      key: companyLabel,
      companyLabel: companyLabel,
      onPick: setPicked
    })) : /*#__PURE__*/React.createElement(LivyPanel, {
      live: live,
      onLive: startLive,
      conversationId: conversationId,
      surface: surface,
      messages: messages,
      streaming: streaming,
      error: error,
      companyLabel: companyLabel,
      starters: view === 'admin' ? STARTERS : SURFACE_STARTERS[view],
      reach: view === 'marketing' ? 'knowledge' : 'account',
      subtitle: SURFACE_SUBTITLE[view],
      showScope: false
      // A resumed resident thread is the transcript alone: the page cannot know whose it was.
      ,
      readOnly: readOnly,
      onSend: send,
      onStop: stop,
      onRetry: retry
    })), /*#__PURE__*/React.createElement(LivyHistory, {
      open: historyOpen,
      onClose: () => setHistoryOpen(false),
      onResume: (id, history, ran) => {
        // Back on the surface it ran on: an admin thread under the customer preview would show
        // reach that surface lacks, and a preview resumed as admin would lose its scope.
        setView(surfaceFromRun(ran));
        setResumedOn(runSurface(ran));
        resume(id, history);
      }
    }))
  );
}

/** Ripples off inside Livy: see LivyTheme. */
export function LivyPage() {
  // The staff page with its view picker for staff; everyone else gets Livy's own full page.
  if (!isLivyAdmin(currentRole())) return /*#__PURE__*/React.createElement(LivyCustomerPage, null);
  return /*#__PURE__*/React.createElement(LivyTheme, null, /*#__PURE__*/React.createElement(LivyPageBody, null));
}